/**
 * DelegationBadge - Shows which specialist agent handled a delegated request.
 *
 * Displayed when Dex (coordinator) delegates a query to a specialist agent.
 * Shows the specialist's name and optionally the delegation time.
 *
 * Blueprint: specs/021-multi-agent-orchestrator/tasks.md (T031)
 */

import React from 'react';
import { Bot, ArrowRight } from 'lucide-react';

export interface DelegationMetadata {
  /** Slug of the specialist agent that handled the request */
  delegated_to: string;
  /** Display name of the specialist agent */
  agent_name: string;
  /** Time taken for delegation in milliseconds */
  delegation_time_ms?: number;
}

/** Agent-specific colors and descriptions, shared across delegation components. */
export const agentStyles: Record<string, { color: string; bgColor: string; description: string }> = {
  chrono: {
    color: 'text-amber-700 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    description: 'Compliance Intelligence',
  },
  dal: {
    color: 'text-blue-700 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    description: 'Data Intelligence',
  },
  humin: {
    color: 'text-pink-700 dark:text-pink-400',
    bgColor: 'bg-pink-50 dark:bg-pink-900/20 border-pink-200 dark:border-pink-800',
    description: 'People Intelligence',
  },
  meti: {
    color: 'text-purple-700 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
    description: 'Knowledge Intelligence',
  },
  provi: {
    color: 'text-emerald-700 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800',
    description: 'Strategic Intelligence',
  },
  tychi: {
    color: 'text-orange-700 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
    description: 'Operational Intelligence',
  },
};

const defaultAgentStyle = {
  color: 'text-gray-700 dark:text-gray-300',
  bgColor: 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700',
  description: 'Specialist',
};

export interface DelegationBadgeProps {
  /** Delegation metadata from the response */
  delegation: DelegationMetadata;
  /** Show the delegation arrow with "via" label (default: true) */
  showArrow?: boolean;
  /** Compact mode - smaller badge (default: false) */
  compact?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Badge showing specialist agent attribution for delegated requests.
 *
 * Example: "Dex → Chrono (Compliance Intelligence)"
 */
export function DelegationBadge({
  delegation,
  showArrow = true,
  compact = false,
  className = '',
}: DelegationBadgeProps) {
  const style = agentStyles[delegation.delegated_to] || defaultAgentStyle;

  if (compact) {
    return (
      <span
        className={`
          inline-flex items-center gap-1 px-1.5 py-0.5
          text-xs font-medium rounded border
          ${style.bgColor} ${style.color}
          ${className}
        `}
        title={`Handled by ${delegation.agent_name} - ${style.description}`}
      >
        <Bot className="w-3 h-3" />
        {delegation.agent_name}
      </span>
    );
  }

  return (
    <div
      className={`
        inline-flex items-center gap-2 px-3 py-1.5
        text-sm rounded-lg border
        ${style.bgColor}
        ${className}
      `}
    >
      {showArrow && (
        <>
          <span className="text-gray-500 dark:text-gray-400 font-medium">Dex</span>
          <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
        </>
      )}
      <div className="flex items-center gap-1.5">
        <div className={`rounded-full p-1 ${style.bgColor}`}>
          <Bot className={`w-3.5 h-3.5 ${style.color}`} />
        </div>
        <span className={`font-semibold ${style.color}`}>
          {delegation.agent_name}
        </span>
        <span className="text-gray-500 dark:text-gray-400">
          ({style.description})
        </span>
      </div>
      {delegation.delegation_time_ms !== undefined && (
        <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
          {delegation.delegation_time_ms}ms
        </span>
      )}
    </div>
  );
}

/**
 * Inline delegation indicator for use within message headers.
 */
export function DelegationIndicator({
  delegation,
  className = '',
}: {
  delegation: DelegationMetadata;
  className?: string;
}) {
  return (
    <DelegationBadge
      delegation={delegation}
      compact
      showArrow={false}
      className={className}
    />
  );
}
